import {
  init,
  backButton,
  hapticFeedback,
  miniApp,
  viewport
} from '@telegram-apps/sdk';

declare global {
  interface Window {
    __isTrnPlay?: () => boolean;
  }
}

export function initTelegram(): void {
  // ── Шаг 1: raw ready() — первый приоритет, работает всегда ──────────
  // Без этого мобильный Telegram показывает чёрный экран загрузки вечно.
  try { (window as any).Telegram?.WebApp?.ready?.(); } catch (_) {}

  // ── Шаг 2: SDK инициализация ────────────────────────────────────────
  try {
    init();

    // Монтируем вьюпорт асинхронно, expand() — только после резолва
    if (viewport.mount.isAvailable()) {
      viewport.mount()
        .then(() => {
          try { viewport.expand(); } catch (_) {}
        })
        .catch(() => {});
    }

    // Монтируем miniApp асинхронно, ready() — только после резолва
    if (miniApp.mount.isAvailable()) {
      miniApp.mount()
        .then(() => {
          try { miniApp.ready(); } catch (_) {}
        })
        .catch(() => {
          // SDK mount упал — fallback через raw API
          try { (window as any).Telegram?.WebApp?.ready?.(); } catch (_) {}
        });
    }

    console.log('Telegram SDK initialized');
  } catch (e) {
    // SDK недоступен (dev-браузер и т.д.) — raw ready() уже вызван выше
    console.log('Running outside Telegram, SDK skipped');
  }
}

let _backHandler: (() => void) | null = null;

// Управление BackButton Telegram
export function setupBackButton(
  currentScreen: string,
  navTo: (screen: string) => void
): void {
  try {
    if (!backButton.mount.isAvailable()) return;

    backButton.mount();

    const screensWithBack = [
      'games', 'game-setup', 'text-game',
      'timer-game', 'emoji-game', 'premium', 'invite', 'profile',
      'tournament-setup', 'tournament-board', 'tournament-victory',
    ];

    if (screensWithBack.includes(currentScreen)) {
      backButton.show();
    } else {
      backButton.hide();
    }

    if (_backHandler) {
      // @ts-expect-error
      backButton.off('click', _backHandler);
    }
    const backMap: Record<string, string> = {
      'games': 'home',
      'game-setup': 'games',
      'text-game': 'games',
      'timer-game': 'games',
      'emoji-game': 'games',
      'premium': 'home',
      'invite': 'home',
      'profile': 'home',
      'tournament-setup': 'home',
      'tournament-board': '__trn_exit__',
      'tournament-victory': 'home',
    };
    _backHandler = () => {
      const isTrnPlaying = (window as any).__isTrnPlay?.() === true;
      const trnScreens = ['timer-game', 'emoji-game', 'text-game'];
      if (isTrnPlaying && trnScreens.includes(currentScreen)) {
        if (typeof (window as any).trnExitConfirm === 'function') {
          (window as any).trnExitConfirm();
          return;
        }
      }

      const target = backMap[currentScreen] || 'home';
      if (target === '__trn_exit__') {
        if (typeof (window as any).trnExitConfirm === 'function') {
          (window as any).trnExitConfirm();
          return;
        }
        navTo('home');
        return;
      }
      navTo(target);
    };
    // @ts-expect-error
    backButton.on('click', _backHandler);
  } catch (e) {
    // Вне Telegram — пропускаем
  }
}

// Утилита для haptic feedback на кнопках
export function tapFeedback(): void {
  try {
    if (typeof hapticFeedback !== 'undefined' && hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred('medium');
    }
  } catch (e) {}
}

export function successFeedback(): void {
  try {
    if (hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred('success');
    }
  } catch (e) {}
}

// ─── Screen Wake Lock ──────────────────────────────────────────────
// Prevents screen from sleeping during active gameplay

let _wakeLock: WakeLockSentinel | null = null;

export async function requestWakeLock(): Promise<void> {
  try {
    if ('wakeLock' in navigator) {
      _wakeLock = await (navigator as any).wakeLock.request('screen');
      _wakeLock?.addEventListener('release', () => {
        _wakeLock = null;
      });
    }
  } catch (e) {
    console.log('Wake Lock not available:', e);
  }
}

export function releaseWakeLock(): void {
  try {
    _wakeLock?.release();
    _wakeLock = null;
  } catch (e) {}
}

// Re-acquire Wake Lock when returning from background (it's released automatically)
document.addEventListener('visibilitychange', async () => {
  if (document.visibilityState === 'visible' && _wakeLock === null) {
    if ((window as any).__gameScreenActive) {
      await requestWakeLock();
    }
  }
});
